import React, { useState, useEffect } from 'react';
import { Images } from '../../utils/imageConstants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setAuthView, setSignupData, setSigninPassword, setGuestMode, closeAuth, setForgotUsername, setPrefilledUsername } from '../../redux/slice/authModalSlice';
import { setProfile } from '../../redux/slice/playerProfileSlice';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema, type LoginFormValues } from './validation/loginSchema';
import {
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import type { LoginProps } from './types/auth';
import { clientRegion, generateSecretHash, setAuthToken, clearGuestMode } from '../../helper/helper';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fetchUserProfile } from '../profile/api/userProfileApi';


const Login: React.FC<LoginProps> = ({
  onGuestMode
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { prefilledUsername } = useAppSelector((s) => s.authModal);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setError,
    setValue,
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { emailOrUserId: '', password: '', keepLoggedIn: false },
  });

  // Pre-fill username if available
  useEffect(() => {
    if (prefilledUsername) {
      setValue('emailOrUserId', prefilledUsername);
      // Clear prefilled username after using it
      dispatch(setPrefilledUsername(undefined));
    }
  }, [prefilledUsername, setValue, dispatch]);

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const secretHash = generateSecretHash(values?.emailOrUserId);
      const command = new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: values?.emailOrUserId,
          PASSWORD: values?.password,
          SECRET_HASH: secretHash,
        },
      });
      const response = await clientRegion.send(command);
      const accessToken = response?.AuthenticationResult?.AccessToken;
      if(response?.$metadata?.httpStatusCode===200 && accessToken){
        setAuthToken(accessToken, values?.keepLoggedIn);
        dispatch(setGuestMode(false));
        clearGuestMode(); // Clear from localStorage
        try{
          const data = await fetchUserProfile();
          dispatch(setProfile(data));
        }catch(err){
          console.warn("Failed to load user profile");
        } 
        dispatch(closeAuth());
      // Close the auth modal
        navigate("/")
        toast.success("Login Successfull")
      }
    } catch (err: any) {
      const errorType = err?.__type || err?.name;
      const errorMessage = err?.message || '';
      
      if (errorType === 'UserNotConfirmedException' || errorMessage.includes('User is not confirmed')) {
        try {
          const secretHash = generateSecretHash(values?.emailOrUserId);
          const resendCommand = new ResendConfirmationCodeCommand({
            ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
            Username: values?.emailOrUserId,
            SecretHash: secretHash,
          });
          
          await clientRegion.send(resendCommand);
          dispatch(setSignupData({ email: values?.emailOrUserId, userId: '', password:'', mobileNumber:'' }));
          dispatch(setSigninPassword(values?.password));
          dispatch(setAuthView('otp'));
          toast.success("Verification code sent to your email");
        } catch (resendErr: any) {
          console.error("Resend confirmation code error:", resendErr);
          toast.error("Failed to send verification code. Please try again.");
          setError('emailOrUserId', { type: 'manual', message: 'Failed to send verification code' });
          setError('password', { type: 'manual' });
        }
      } else {
        const message = errorMessage || 'Sign-in failed';
        setError('emailOrUserId', { type: 'manual', message });
        setError('password', { type: 'manual' });
      }
    }
  };

  const handleForgotUsername = () => {
    dispatch(setForgotUsername(true));
    dispatch(setAuthView('forgot'));
  };

  const handleForgotPassword = () => {
    dispatch(setForgotUsername(false));
    dispatch(setAuthView('forgot'));
  };

  const handleBack = () => {
    dispatch(closeAuth());
    if (location.pathname === '/login') {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
           <div className="login-modal-container">
        <button
          type="button"
          className="auth-screen-back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={Images.back_arrow} alt="" aria-hidden />
        </button>
        <div className="login-header">
          <div className="logo-container">
            <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
          </div>
          <h1 className="app-name">CRICCENTER</h1>
        </div>
        <h2 className="signin-title">Sign In</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
          <div className="form-group">
            <label htmlFor="emailOrUserId" className="form-label">
              Email / User Id
            </label>
            <div className="input-wrapper">
              <input
                id="emailOrUserId"
                type="text"
                className={`form-input ${errors.emailOrUserId ? 'error' : ''}`}
                placeholder="Enter Email Address or User Id"
                {...register('emailOrUserId')}
              />
            </div>
            {errors.emailOrUserId && (
              <span className="error-message">{errors.emailOrUserId.message as string}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter Password"
                {...register('password')}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {showPassword ? (
                    <path
                      d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                      fill="currentColor"
                    />
                  ) : (
                    <path
                      d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                      fill="currentColor"
                    />
                  )}
                </svg>
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password.message as string}</span>
            )}
          </div>
          <div className="options-row">
            <label className="checkbox-container">
              <input
                type="checkbox"
                {...register('keepLoggedIn')}
              />
              <span className="checkmark"></span>
              <span className="checkbox-label">Keep me logged in</span>
            </label>
            
            <div className="forgot-links">
              <button
                type="button"
                className="forgot-link"
                onClick={handleForgotUsername}
              >
                Forgot Username?
              </button>
              <button
                type="button"
                className="forgot-link"
                onClick={handleForgotPassword}
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <div className="action-buttons">
            <button type="submit" className="btn-primary" disabled={isSubmitting || !isValid}>
              Log In
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={onGuestMode}
            >
              Explore As Guest
            </button>
          </div>

          <div className="signup-section">
            <span className="signup-text">
              Don't have an account?{' '}
              <button
                type="button"
                className="signup-link"
                onClick={() => dispatch(setAuthView('signup'))}
              >
                Sign Up
              </button>
            </span>
          </div>
        </form>
      </div>
        </div> 
       
    </div>
  );
};

export default Login;
